// Zod schemas shared between web and api.
// As CRUD endpoints are introduced (Steps 4–11), define their request/response
// schemas here so both sides reference the same shape.

import { z } from 'zod';

export const QuestionType = z.enum([
  'yesno',
  'text',
  'select',
  'multiselect',
  'document_adopt',
  'document_upload',
  'multiselect_uploads',
  'signature',
]);
export type QuestionType = z.infer<typeof QuestionType>;

export const CompanyStatus = z.enum([
  'invited',
  'in_progress',
  'review',
  'changes',
  'compliant',
  'expired',
]);
export type CompanyStatus = z.infer<typeof CompanyStatus>;

export const SubmissionStatus = z.enum(['pending', 'approved', 'rejected']);
export type SubmissionStatus = z.infer<typeof SubmissionStatus>;

export const AdoptionMode = z.enum(['adopted', 'uploaded']);
export type AdoptionMode = z.infer<typeof AdoptionMode>;

export const ActorType = z.enum(['admin', 'company', 'system']);
export type ActorType = z.infer<typeof ActorType>;

// ----------------------------------------------------------------------
// Questions / adoption triggers
// ----------------------------------------------------------------------

export const QuestionCondition = z.object({
  value: z.union([z.string(), z.number(), z.boolean(), z.array(z.string())]),
});
export type QuestionCondition = z.infer<typeof QuestionCondition>;

export const AdoptionTriggerInput = z.object({
  trigger_value: z.string().min(1),
  document_id: z.string().uuid(),
  adopt_only: z.boolean().default(false),
});
export type AdoptionTriggerInput = z.infer<typeof AdoptionTriggerInput>;

const baseQuestionFields = {
  type: QuestionType,
  category_id: z.string().uuid(),
  label: z.string().min(1),
  help_text: z.string().nullable().optional(),
  required: z.boolean().optional(),
  requires_upload: z.boolean().optional(),
  upload_label: z.string().nullable().optional(),
  options: z.array(z.string()).nullable().optional(),
  parent_id: z.string().uuid().nullable().optional(),
  condition: QuestionCondition.nullable().optional(),
  adoptable_doc_id: z.string().uuid().nullable().optional(),
  display_order: z.number().int().min(0).optional(),
  adoption_triggers: z.array(AdoptionTriggerInput).optional(),
};

export const QuestionCreateInput = z
  .object(baseQuestionFields)
  .superRefine(crossFieldChecks);
export type QuestionCreateInput = z.infer<typeof QuestionCreateInput>;

export const QuestionUpdateInput = z
  .object({
    type: QuestionType.optional(),
    category_id: z.string().uuid().optional(),
    label: z.string().min(1).optional(),
    help_text: z.string().nullable().optional(),
    required: z.boolean().optional(),
    requires_upload: z.boolean().optional(),
    upload_label: z.string().nullable().optional(),
    options: z.array(z.string()).nullable().optional(),
    parent_id: z.string().uuid().nullable().optional(),
    condition: QuestionCondition.nullable().optional(),
    adoptable_doc_id: z.string().uuid().nullable().optional(),
    display_order: z.number().int().min(0).optional(),
    adoption_triggers: z.array(AdoptionTriggerInput).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.type !== undefined) crossFieldChecks(value as never, ctx);
  });
export type QuestionUpdateInput = z.infer<typeof QuestionUpdateInput>;

export const QuestionsReorderInput = z.object({
  ids: z.array(z.string().uuid()).min(1),
});
export type QuestionsReorderInput = z.infer<typeof QuestionsReorderInput>;

function crossFieldChecks(
  value: {
    type?: QuestionType | undefined;
    parent_id?: string | null | undefined;
    condition?: QuestionCondition | null | undefined;
    adoptable_doc_id?: string | null | undefined;
    options?: string[] | null | undefined;
  },
  ctx: z.RefinementCtx,
): void {
  if (value.parent_id && !value.condition) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['condition'],
      message: 'condition is required when parent_id is set',
    });
  }
  if (!value.parent_id && value.condition) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['condition'],
      message: 'condition only allowed when parent_id is set',
    });
  }
  if (
    value.adoptable_doc_id &&
    value.type !== 'document_adopt' &&
    value.type !== 'document_upload'
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['adoptable_doc_id'],
      message: 'adoptable_doc_id only valid for document_adopt or document_upload',
    });
  }
  if (
    (value.type === 'select' ||
      value.type === 'multiselect' ||
      value.type === 'multiselect_uploads') &&
    (!value.options || value.options.length === 0)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['options'],
      message: 'options is required for select / multiselect questions',
    });
  }
}

// ----------------------------------------------------------------------
// Question categories
// ----------------------------------------------------------------------

export const QuestionCategoryCreateInput = z.object({
  name: z.string().min(1).max(255),
  display_order: z.number().int().min(0).optional(),
});
export type QuestionCategoryCreateInput = z.infer<typeof QuestionCategoryCreateInput>;

export const QuestionCategoryUpdateInput = z
  .object({
    name: z.string().min(1).max(255).optional(),
    display_order: z.number().int().min(0).optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type QuestionCategoryUpdateInput = z.infer<typeof QuestionCategoryUpdateInput>;

export const QuestionCategoriesReorderInput = z.object({
  ids: z.array(z.string().uuid()).min(1),
});
export type QuestionCategoriesReorderInput = z.infer<typeof QuestionCategoriesReorderInput>;

// ----------------------------------------------------------------------
// Companies
// ----------------------------------------------------------------------

const companyInfoFields = {
  contractor_type: z.string().max(255).nullable().optional(),
  vendor_number: z.string().max(255).nullable().optional(),
  contract_number: z.string().max(255).nullable().optional(),
  cluster_region: z.string().max(255).nullable().optional(),
  station: z.string().max(255).nullable().optional(),
  abn: z.string().max(255).nullable().optional(),
  trustee_info: z.string().max(1000).nullable().optional(),
  primary_contact_name: z.string().max(255).nullable().optional(),
  contact_role: z.string().max(255).nullable().optional(),
  contact_phone: z.string().max(50).nullable().optional(),
  contact_email: z.string().email().max(255).nullable().optional(),
  employee_count: z.number().int().min(0).max(10_000_000).nullable().optional(),
};

export const CompanyCreateInput = z.object({
  company_name: z.string().min(1).max(255),
  contact_name: z.string().min(1).max(255),
  email: z.string().email().max(255),
  ...companyInfoFields,
});
export type CompanyCreateInput = z.infer<typeof CompanyCreateInput>;

export const CompanyUpdateInput = z
  .object({
    company_name: z.string().min(1).max(255).optional(),
    contact_name: z.string().min(1).max(255).optional(),
    email: z.string().email().max(255).optional(),
    status: CompanyStatus.optional(),
    expires_at: z.string().datetime().nullable().optional(),
    ...companyInfoFields,
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type CompanyUpdateInput = z.infer<typeof CompanyUpdateInput>;

// Subset the company itself can edit through the portal — must not allow
// changing pre-filled fields (contractor_type, vendor_number, contract_number,
// cluster_region, station) or status / contact_name / email used for invites.
export const CompanyInfoSelfUpdateInput = z
  .object({
    abn: z.string().max(255).nullable().optional(),
    trustee_info: z.string().max(1000).nullable().optional(),
    primary_contact_name: z.string().max(255).nullable().optional(),
    contact_role: z.string().max(255).nullable().optional(),
    contact_phone: z.string().max(50).nullable().optional(),
    contact_email: z.string().email().max(255).nullable().optional(),
    employee_count: z.number().int().min(0).max(10_000_000).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type CompanyInfoSelfUpdateInput = z.infer<typeof CompanyInfoSelfUpdateInput>;

export const CompanyInviteInput = z.object({
  ttl_days: z.number().int().min(1).max(60).optional(),
});
export type CompanyInviteInput = z.infer<typeof CompanyInviteInput>;

// ----------------------------------------------------------------------
// Review actions (admin)
// ----------------------------------------------------------------------

export const ReviewDecision = z.enum(['approve', 'reject']);
export type ReviewDecision = z.infer<typeof ReviewDecision>;

export const ReviewInput = z
  .object({
    decision: ReviewDecision,
    comment: z.string().min(1).max(2000).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.decision === 'reject' && !value.comment) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['comment'],
        message: 'comment is required when requesting changes',
      });
    }
  });
export type ReviewInput = z.infer<typeof ReviewInput>;

export const BulkApproveInput = z
  .object({
    expires_in_days: z.number().int().min(1).max(3650).optional(),
  })
  .optional();
export type BulkApproveInput = z.infer<typeof BulkApproveInput>;

// ----------------------------------------------------------------------
// Submissions
// ----------------------------------------------------------------------

export const SignatureAnswer = z.object({
  signature_data_url: z.string().min(1),
  signer_name: z.string().min(1),
  signed_at: z.string().datetime(),
});
export type SignatureAnswer = z.infer<typeof SignatureAnswer>;

export const SubmissionAnswerInput = z.object({
  answer: z
    .union([
      z.string(),
      z.number(),
      z.boolean(),
      z.array(z.string()),
      SignatureAnswer,
      z.null(),
    ])
    .optional(),
  comment: z.string().nullable().optional(),
});
export type SubmissionAnswerInput = z.infer<typeof SubmissionAnswerInput>;

export const SubmissionFileUploadUrlInput = z.object({
  file_name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(255),
  option_key: z.string().min(1).max(255).nullable().optional(),
});
export type SubmissionFileUploadUrlInput = z.infer<typeof SubmissionFileUploadUrlInput>;

export const SubmissionFileRegisterInput = z.object({
  file_path: z.string().min(1),
  file_name: z.string().min(1).max(255),
  size_bytes: z.number().int().min(0),
  mime_type: z.string().min(1).max(255),
  option_key: z.string().min(1).max(255).nullable().optional(),
});
export type SubmissionFileRegisterInput = z.infer<typeof SubmissionFileRegisterInput>;

export const SubmissionAdoptionInput = z.discriminatedUnion('mode', [
  z.object({
    mode: z.literal('adopted'),
    option_key: z.string().min(1).max(255).nullable().optional(),
  }),
  z.object({
    mode: z.literal('uploaded'),
    file_path: z.string().min(1),
    option_key: z.string().min(1).max(255).nullable().optional(),
  }),
]);
export type SubmissionAdoptionInput = z.infer<typeof SubmissionAdoptionInput>;

// ----------------------------------------------------------------------
// Documents
// ----------------------------------------------------------------------

export const DocumentUploadUrlInput = z.object({
  file_name: z.string().min(1).max(255),
  mime_type: z.string().min(1).max(255),
});
export type DocumentUploadUrlInput = z.infer<typeof DocumentUploadUrlInput>;

export const DocumentCreateInput = z.object({
  name: z.string().min(1),
  category: z.string().min(1),
  version: z.string().min(1),
  file_path: z.string().min(1),
  is_adoptable: z.boolean().default(true),
});
export type DocumentCreateInput = z.infer<typeof DocumentCreateInput>;

export const DocumentUpdateInput = z
  .object({
    name: z.string().min(1).optional(),
    category: z.string().min(1).optional(),
    version: z.string().min(1).optional(),
    is_adoptable: z.boolean().optional(),
    current_version: z.boolean().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field is required' });
export type DocumentUpdateInput = z.infer<typeof DocumentUpdateInput>;

// ----------------------------------------------------------------------
// Branding
// ----------------------------------------------------------------------
export const CompanyInfoLabels = z
  .object({
    contractorType: z.string().min(1).max(100).optional(),
    vendorNumber: z.string().min(1).max(100).optional(),
    contractNumber: z.string().min(1).max(100).optional(),
    clusterRegion: z.string().min(1).max(100).optional(),
    station: z.string().min(1).max(100).optional(),
    primaryContact: z.string().min(1).max(100).optional(),
  })
  .optional();
export type CompanyInfoLabels = z.infer<typeof CompanyInfoLabels>;

export const Branding = z.object({
  companyName: z.string(),
  tagline: z.string().optional(),
  logoUrl: z.string().url().optional(),
  primaryColor: z
    .string()
    .regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/),
  primaryDark: z.string().regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/),
  accentColor: z.string().regex(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/),
  welcomeHeading: z.string(),
  welcomeMessage: z.string(),
  emailFromName: z.string(),
  emailFromAddress: z.string().email(),
  companyInfoLabels: CompanyInfoLabels,
  stationOptions: z.array(z.string().min(1).max(120)).optional(),
});
export type Branding = z.infer<typeof Branding>;

export const COMPANY_INFO_LABEL_DEFAULTS = {
  contractorType: 'Contractor Type',
  vendorNumber: 'Vendor Number',
  contractNumber: 'Contract Number',
  clusterRegion: 'Cluster Region',
  station: 'Station',
  primaryContact: 'Primary Contact',
} as const;
